import { z } from "zod";
import { LanguageLabelTransformer } from "../transformers/language.transformers";
import { RegionLabelTransformer } from "../transformers/region.transformers";
import { AgencyTypeFieldsSchema, AgencyTypeSchema, AppTierSchema, ListingTypeSchema, PackagedServicePriceTypeSchema, ShopifyPartnerTierSchema } from "./shared";

// Service structure from API response
export const ExpertServiceSchema = z.object({
    name: z.string(),
    url: z.string(),
});

// Partner structure from API response
export const ExpertPartnerSchema = z.object({
    name: z.string(),
    slug: z.string(),
    logo: z.string(),
});

// Packaged service structure from API response
export const ExpertPackagedServiceSchema = z.object({
    id: z.string(),
    slug: z.string().nullable().optional().default('').transform((val) => val || ''),
    title: z.string(),
    price: z.number(),
    price_type: PackagedServicePriceTypeSchema.nullable().optional().default('fixed'),
    imageUrl: z.string().optional().nullable().default('').transform((val) => val || ''),
    gallery_images: z.array(z.string()).nullable().optional().default([]),
    description: z.string().optional().nullable().transform((val) => val || undefined),
});

// Location structure from API response
export const ExpertLocationSchema = z.object({
    address: z.string().optional().nullable().default('').transform((val) => val || ''),
    countryAndCity: z.string().optional().nullable().default('').transform((val) => val ? RegionLabelTransformer(val) : ''),
    countryCode: z.string().optional().nullable().default(''),
    city: z.string().optional().nullable().transform((val) => val || undefined),
    latitude: z.number().optional().nullable().transform((val) => val || undefined),
    longitude: z.number().optional().nullable().transform((val) => val || undefined),
    verified: z.boolean().default(false),
});

// Social links structure from API response
export const ExpertSocialLinkSchema = z.object({
    name: z.enum(['linkedin', 'twitter', 'github', 'x', 'fiverr', 'upwork', 'shopify-directory', 'book-call']),
    href: z.string(),
});

// Representative structure (for agencies)
export const ExpertRepresentativeSchema = z.object({
    picture: z.string().optional().nullable().transform((val) => val || undefined),
    name: z.string().optional().nullable().transform((val) => val || ''),
    role: z.string().optional().nullable().transform((val) => val || ''),
});

// Overall ratings breakdown
export const ExpertOverallRatingsSchema = z.object({
    qualityOfWork: z.number().optional().default(0),
    communication: z.number().optional().default(0),
    timelines: z.number().optional().default(0),
    valueForMoney: z.number().optional().default(0),
});

// Customer story structure from API response
export const ExpertCustomerStorySchema = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string().optional().nullable().default('').transform((val) => val || ''),
    period: z.string().optional().nullable().default('').transform((val) => val || ''),
    thumbnail: z.string().optional().nullable().default('').transform((val) => val || ''),
    images: z.array(z.string()),
    hiredOnShopexperts: z.boolean(),
});

// FAQ structure from API response
export const ExpertFaqSchema = z.object({
    id: z.string(),
    question: z.string(),
    answer: z.string(),
});

// Main expert details schema
export const ExpertDetailsSchema = z.object({
    id: z.string(),
    slug: z.string().optional().default('').transform((val) => val || ''),
    avatar: z.string().optional().nullable().default('').transform((val) => val || ''),
    name: z.string().optional().nullable().default('').transform((val) => val || ''),
    type: z.enum(['freelancer', 'agency']),
    listing_type: ListingTypeSchema.optional(),
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    partnerTier: ShopifyPartnerTierSchema.nullable().optional().default('none'),
    appTier: AppTierSchema.optional(),
    title: z.string().optional().nullable().default('').transform((val) => val || ''),
    // Legacy fallback for `freelancer_role` on payloads that don't fold
    // it into `title`. Consumers should read `title || role`.
    role: z.string().optional().nullable().default('').transform((val) => val || ''),
    isPro: z.boolean(),
    price: z.number().nullable().optional().default(0).transform((val) => val ?? 0),
    services: z.array(ExpertServiceSchema).optional().nullable().default([]).transform((val) => val || []),
    partners: z.array(ExpertPartnerSchema).optional().nullable().default([]).transform((val) => val || []),
    description: z.string().nullable().optional().transform((val) => val || ''),
    geo_regions: z.array(z.string()).nullable().optional().default([]).transform((val) => val || []),
    regionsServed: z.array(z.string()).nullable().optional().default([]).transform((val) => val && val.map(RegionLabelTransformer) || []),
    phoneNumber: z.string().optional().nullable().default('').transform((val) => val || ''),
    website: z.string().optional().nullable().default('').transform((val) => val || ''),
    bookingCallUrl: z.string().optional().nullable().default('').transform((val) => val || ''),
    booking_call_url: z.string().optional().nullable().default('').transform((val) => val || ''),
    packagedServices: z.array(ExpertPackagedServiceSchema).optional().nullable().default([]).transform((val) => val || []),
    createdAt: z.string().nullable().optional().transform((val) => val || undefined),
    responseTime: z.string().optional().nullable().default('').transform((val) => val || ''),
    timezone: z.string().nullable().optional().default('').transform((val) => val || ''),
    location: ExpertLocationSchema,
    socialLinks: z.array(ExpertSocialLinkSchema)
        .default([])
        .transform((links) => links.filter((link) => link.name !== 'book-call')),
    languages: z.array(z.string()).optional().default([]).transform((val) => val.map(LanguageLabelTransformer)),
    representative: ExpertRepresentativeSchema.nullable().optional().transform((val) => val || undefined),
    overallReviewAverage: z.number().optional().default(0),
    overallReviewsCount: z.number().optional().default(0),
    overallRatings: ExpertOverallRatingsSchema,
    customerStories: z.array(ExpertCustomerStorySchema),
    faqs: z.array(ExpertFaqSchema),
});

export type ExpertService = z.infer<typeof ExpertServiceSchema>;
export type ExpertPartner = z.infer<typeof ExpertPartnerSchema>;
export type ExpertPackagedService = z.infer<typeof ExpertPackagedServiceSchema>;
export type ExpertLocation = z.infer<typeof ExpertLocationSchema>;
export type ExpertSocialLink = z.infer<typeof ExpertSocialLinkSchema>;
export type ExpertRepresentative = z.infer<typeof ExpertRepresentativeSchema>;
export type ExpertOverallRatings = z.infer<typeof ExpertOverallRatingsSchema>;
export type ExpertCustomerStory = z.infer<typeof ExpertCustomerStorySchema>;
export type ExpertFaq = z.infer<typeof ExpertFaqSchema>;
export type ExpertDetails = z.infer<typeof ExpertDetailsSchema>;
